/* eslint-disable react-hooks/exhaustive-deps */
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useBillingCompanyContext } from "../../providers";
import { useDispatchWrapper } from "../../hooks";
import { ISubscriptionPlan } from "../../redux/subscription/subscription.types";
import {
  fetchSubscriptionPlansAsync,
  getCompanyOutletSubscriptionPlan,
  getUpcomingSubscriptionStatusAsync,
  ISubscriptionPlansResult,
  upserCompanyOutletSubscriptionPlan,
} from "../../services/subcriptionApi";
import { PageContainer } from "../../components/container/PageContainer";
import { Dialog } from "../../components/Dialogs/Dialog";
import { useAuth } from "../../contexts/AuthProvider/AuthProvider";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../redux/messages/messagesActions";
import { SuccessDialog } from "../../components/Dialogs/SuccessDialog";
import { SubscriptionpaymentSuccessDialog } from "./SubscriptionpaymentSuccessDialog";
import { useSubscriptionError } from "./hooks/useSubscriptionError";
import Navbar from "../authentication/Navbar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../../api/api";
import { server_base_endpoints } from "../../api/server-base-endpoints";
import { OrderPreviewDialog, BillingMode } from "./OrderPreviewDialog";
import { StandardCard } from "../../components/Cards";
import { Breadcrumb } from "../../layout/fullLayout/breadcrumb/Breadcrumb";
import { IBreadcrumbProps } from "../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { IStoreState } from "../../redux/initialStoreState";
import { useSelector } from "react-redux";
import { fetchCompanyOutletAsync } from "../../redux/companyOutlet/companyOutletActions";
import { loadRazorpay } from "../../utils/loadRazorpay";
import { ChangeBillingModeDialog, BillingMode as CurrentBillingMode } from "./changeBillingMode";
import { TrialPlanBanner } from "./components/TrialPlanBanner";
import { PricingPlanCard, PricingPlanCardSkeleton } from "./components/PricingPlanCard";
import { PRICING_ACCENT } from "./pricingTheme";

export const getPlanIdFromPlan = (
  price: ISubscriptionPlan["price"],
  selectedCurrency: "INR" | "USD",
): string => {
  if (!Array.isArray(price)) return "";
  return price.find((p) => p.currency === selectedCurrency)?.plan_id ?? "";
};

const parseAddonDescription = (
  html: string,
): { intro: string; features: string[] } => {
  if (!html) return { intro: "", features: [] };
  const text = html
    .replace(/<\/(p|div|li|br)>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
  const segments = text.split("✅");
  const intro = segments[0]?.trim() ?? "";
  const features = segments.slice(1).map((s) => s.trim()).filter(Boolean);
  return { intro, features };
};

interface ISubscriptionPlanSelectorProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export const SubscriptionPlanSelector: React.FC<ISubscriptionPlanSelectorProps> = ({
  onSuccess,
  onError,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams<{ uuid: string }>();

  const subscriptionError = useSubscriptionError();
  const companyOutlet = useBillingCompanyContext();
  const { user, subscribed_plan, checkUserSession } = useAuth();
  const dispatch = useDispatchWrapper();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // --- ROLE & MODE DETECTION ---
  const isEnrAdmin = user.role_value === "ENR_ADMIN";
  const isAdminAssign = isEnrAdmin && !!uuid;
  const outletUuid = uuid || companyOutlet.comapny.company_outlet_uuid;
  const showShareButton = isEnrAdmin && !!uuid;

  // Red "Banquet First" theme applies only on the public pricing route.
  const isPricingRoute = location.pathname === "/subscription/pricing";
  // Header override for dialogs so they match the red theme on the pricing page.
  const dialogAccentTitleSx = isPricingRoute ? { bgcolor: PRICING_ACCENT.main } : undefined;
  // Primary (contained) action-button override so footer CTAs match the red theme.
  const dialogAccentButtonSx = isPricingRoute
    ? {
        bgcolor: PRICING_ACCENT.main,
        color: "#fff",
        "&:hover": { bgcolor: PRICING_ACCENT.dark },
        "&.Mui-disabled": { bgcolor: PRICING_ACCENT.main, color: "#fff", opacity: 0.5 },
      }
    : undefined;




  // --- COMPANY OUTLET DATA (for admin banner) ---
  const { data: companyOutletData } = useSelector(
    (storeState: IStoreState) => storeState.companyOutlet.companyOutlet,
  );

  // --- STATE ---
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<ISubscriptionPlansResult>({
    subscriptionPlans: [],
    nextSubscriptionPlan: null,
  });
  const [fetchedOutletPlanId, setFetchedOutletPlanId] = useState<string | null>(null);
  const [paymentSuccessDialog, setPaymentSuccessDialog] = useState(false);
  const [openAddonDialog, setOpenAddonDialog] = React.useState<ISubscriptionPlan | null>(null);
  const [addonDialogType, setAddonDialogType] = React.useState<"BUY" | "SHARE">("BUY");
  const [addonLoadState, setAddonLoadState] = React.useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [availableAddons, setAvailableAddons] = React.useState<
    Array<{
      addon_master_uuid?: string | null;
      addon_name: string;
      module_master_uuid: string;
      module_endpoint: string;
      endpoint_method: string;
      table_name: string;
      table_identifier_column: string;
      extra_count: number;
      price: Array<{ currency: string; amount: number }>;
      description: string;
      status: "ACTIVE" | "INACTIVE";
      billing_cycle?: string | null;
    }>
  >([]);
  const [selectedAddons, setSelectedAddons] = React.useState<
    Record<string, { addon: (typeof availableAddons)[number]; addon_count: number }>
  >({});
  const [upserting, setUpserting] = React.useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = React.useState<ISubscriptionPlan | null>(null);
  const [billingMode, setBillingMode] = React.useState<BillingMode>("RECURRING");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [billingCycleFilter, setBillingCycleFilter] = useState<"months" | "years">("months");
  const [changeModePlan, setChangeModePlan] = React.useState<ISubscriptionPlan | null>(null);

  // --- SELECTED PLAN ID ---
  const selectedPlanId = isAdminAssign
    ? fetchedOutletPlanId
    : subscribed_plan?.subscription_plan_uuid || null;

  // --- URL MESSAGE ---
  const urlMessage = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get("message");
    if (!message) return null;
    const decoded = decodeURIComponent(message);
    // Suppress the generic "no subscription" notice — expected on this page.
    if (/no\s*subscription\s*found/i.test(decoded)) return null;
    return decoded;
  }, [location.search]);

  // --- RESOLVE ADDON PLAN → PARENT ---
  const resolvedSelectedPlanId = React.useMemo(() => {
    if (!selectedPlanId) return null;
    const selectedPlan = subscriptions.subscriptionPlans.find(
      (p) => p.subscription_plan_uuid === selectedPlanId,
    );
    if (selectedPlan && selectedPlan.is_plan_addon === 1 && selectedPlan.parent_subscription_uuid) {
      return selectedPlan.parent_subscription_uuid;
    }
    return selectedPlanId;
  }, [selectedPlanId, subscriptions.subscriptionPlans]);

  // --- DATA FETCHING ---
  const fetchPlans = React.useCallback(async () => {
    setSubscriptionLoading(true);
    fetchSubscriptionPlansAsync()
      .then((result) => {
        setSubscriptions(result);
      })
      .finally(() => {
        setSubscriptionLoading(false);
      });
  }, [outletUuid]);

  const fetchOutletPlan = React.useCallback(async () => {
    if (!isAdminAssign || !uuid) return;
    try {
      const response = await getCompanyOutletSubscriptionPlan("All", uuid);
      if (response && response[0]?.subscription_plan_uuid) {
        setFetchedOutletPlanId(response[0].subscription_plan_uuid);
      }
    } catch (error) {
      console.error("Error fetching outlet subscription:", error);
    }
  }, [isAdminAssign, uuid]);

  const fetchAddons = React.useCallback(async () => {
    setAddonLoadState("loading");
    try {
      const res = await api.get(server_base_endpoints.subscriptions.addons.get);
      const payload = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(payload) ? payload : [payload];
      const normalized = list
        .filter(Boolean)
        .map((raw: any) => ({
          addon_master_uuid: raw?.addon_master_uuid ?? null,
          addon_name: String(raw?.addon_name ?? ""),
          module_master_uuid: String(raw?.module_master_uuid ?? ""),
          module_endpoint: String(raw?.module_endpoint ?? ""),
          endpoint_method: String(raw?.endpoint_method ?? ""),
          table_name: String(raw?.table_name ?? ""),
          table_identifier_column: String(raw?.table_identifier_column ?? ""),
          extra_count: Number(raw?.extra_count ?? 0),
          price: Array.isArray(raw?.price) && raw.price.length > 0
            ? raw.price.map((p: any) => ({ currency: String(p?.currency ?? ""), amount: Number(p?.amount ?? 0) }))
            : [{ currency: "INR", amount: 0 }],
          description: String(raw?.description ?? ""),
          status: (raw?.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
          billing_cycle: raw?.billing_cycle ?? null,
        }))
        .filter((a: any) => a.status === "ACTIVE");
      setAvailableAddons(normalized);
      setAddonLoadState("loaded");
    } catch (e: any) {
      setAddonLoadState("error");
      dispatch(showMessage({ displayAs: "snackbar", type: "error", message: e?.response?.data?.message || "Failed to load add-ons" }));
    }
  }, [dispatch]);

  const getAddonPrice = React.useCallback(
    (addon: (typeof availableAddons)[number], currency: "INR" | "USD" = "INR") => {
      const wanted = String(currency || "INR").toUpperCase();
      const row = addon.price?.find((p) => String(p.currency || "").toUpperCase() === wanted) ?? addon.price?.[0];
      return Number(row?.amount ?? 0);
    },
    [availableAddons],
  );

  // --- EFFECTS ---
  React.useEffect(() => {
    fetchPlans();
    fetchAddons();
    if (isAdminAssign) {
      dispatch(fetchCompanyOutletAsync(uuid!));
      fetchOutletPlan();
    }
  }, [outletUuid]);

  React.useEffect(() => {
    if (!openAddonDialog) return;
    fetchAddons();
    setSelectedAddons({});
  }, [openAddonDialog]);

  // Payment polling (normal user mode only)
  React.useEffect(() => {
    if (isAdminAssign) return;
    const subscription = subscriptions.nextSubscriptionPlan;
    if (!outletUuid || !subscription || subscription.status !== "PENDING") return;

    const intervalId = setInterval(async () => {
      try {
        const result = await getUpcomingSubscriptionStatusAsync(outletUuid);
        setSubscriptions((prev) => ({ ...prev, nextSubscriptionPlan: result }));
        if (result?.status === "PAID") setPaymentSuccessDialog(true);
        if (result?.status !== "PENDING") clearInterval(intervalId);
      } catch (err) {
        console.error("Failed to fetch subscription status", err);
      }
    }, 10000);
    return () => clearInterval(intervalId);
  }, [outletUuid, subscriptions.nextSubscriptionPlan?.status, isAdminAssign]);

  // --- COMPUTED ---
  const displayedPlans = useMemo(() => {
    const filtered = subscriptions.subscriptionPlans.filter(
      (plan) =>
        (!plan.billing_cycle || plan.billing_cycle === billingCycleFilter) &&
        plan.is_plan_addon !== 1 &&
        !Boolean(plan.is_trial), // trial plan is surfaced separately below the grid
    );
    return filtered.sort((a, b) => {
      const priceA = Array.isArray(a.price) ? Number(a.price[0]?.base_amount ?? 0) : 0;
      const priceB = Array.isArray(b.price) ? Number(b.price[0]?.base_amount ?? 0) : 0;
      return priceA - priceB;
    });
  }, [subscriptions.subscriptionPlans, billingCycleFilter]);

  // Trial plan rendered as an "or continue with trial" strip under the grid.
  const trialPlan = useMemo(
    () =>
      subscriptions.subscriptionPlans.find(
        (plan) => Boolean(plan.is_trial) && plan.is_plan_addon !== 1,
      ) ?? null,
    [subscriptions.subscriptionPlans],
  );

  const activeAddonPlans = useMemo(() => {
    if (!selectedPlanId || availableAddons.length === 0) return [];
    const subscribedPlan = subscriptions.subscriptionPlans.find((p) => p.subscription_plan_uuid === selectedPlanId);
    const basePlan = resolvedSelectedPlanId !== selectedPlanId
      ? subscriptions.subscriptionPlans.find((p) => p.subscription_plan_uuid === resolvedSelectedPlanId)
      : null;
    const addonUuids = new Set<string>();
    if (subscribedPlan?.addon_uuids) subscribedPlan.addon_uuids.forEach((u) => addonUuids.add(u));
    if (basePlan?.addon_uuids) basePlan.addon_uuids.forEach((u) => addonUuids.add(u));
    if (addonUuids.size === 0) return [];
    return availableAddons
      .filter((addon) => addon.addon_master_uuid && addonUuids.has(addon.addon_master_uuid))
      .map((addon) => ({
        plan_name: addon.addon_name,
        price: addon.price as any,
        billing_cycle: (subscribedPlan?.billing_cycle || basePlan?.billing_cycle || undefined) as string | undefined,
      }));
  }, [subscriptions.subscriptionPlans, selectedPlanId, resolvedSelectedPlanId, availableAddons]);

  const getStepText = resolvedSelectedPlanId ? "Update Subscription Plan" : "Select Subscription Plan";

  // --- HANDLERS ---
  const toggleAddon = React.useCallback(
    (key: string, addon: (typeof availableAddons)[number]) => {
      setSelectedAddons((prev) => {
        const copy = { ...prev };
        if (copy[key]) { delete copy[key]; return copy; }
        copy[key] = { addon, addon_count: addon.extra_count ?? 0 };
        return copy;
      });
    }, [],
  );

  async function handleChoosePlan(plan: ISubscriptionPlan) {
    setAddonDialogType("BUY");
    setOpenAddonDialog(plan);
  }

  async function handleSharePlan(plan: ISubscriptionPlan) {
    setAddonDialogType("SHARE");
    setOpenAddonDialog(plan);
  }

  /** True when any currently selected add-on has a non-zero price. */
  const hasPaidAddons = React.useCallback(
    (currency: "INR" | "USD" = "INR") =>
      Object.values(selectedAddons).some(({ addon, addon_count }) => {
        if (!Number(addon_count)) return false;
        const wanted = String(currency).toUpperCase();
        const row =
          addon.price?.find((p) => String(p.currency || "").toUpperCase() === wanted) ??
          addon.price?.[0];
        return Number(row?.amount ?? 0) > 0;
      }),
    [selectedAddons],
  );

  /** A plan is "free" when it is flagged free or its base price is zero. */
  const isPlanFree = React.useCallback((plan: ISubscriptionPlan) => {
    if (Boolean(plan.is_free_plan)) return true;
    const entry = Array.isArray(plan.price)
      ? plan.price.find((p) => p.currency === "INR") ?? plan.price[0]
      : undefined;
    return Number(entry?.base_amount ?? 0) === 0;
  }, []);

  /**
   * Opens the Razorpay checkout for a successful upsert response.
   * Handles both RECURRING (subscription) and PREPAID (order) flows.
   */
  const launchRazorpayCheckout = async (data: any, plan: ISubscriptionPlan) => {
    const {
      billing_mode: respBillingMode,
      rzp_type,
      rzp_key: resporzp_key,
      gateway_subscription_id,
      sub_id,
      rzp_order,
    } = data;

    /* RECURRING → Razorpay subscription (rzp_type "subscription").
       PREPAID   → Razorpay one-time order/checkout (rzp_type "checkout"). */
    const isSubscription = rzp_type === "subscription" || respBillingMode === "RECURRING";

    const baseOptions = {
      key: resporzp_key || "",
      name: plan.plan_name,
      description: plan.description,
      image: "/logo.png",
      handler: function (_response: any) {
        setOpenSuccess(true);
        fetchOutletPlan();
      },
      prefill: { name: user.name, email: user.email, contact: "" },
      theme: { color: "#0074d4" },
    };

    let options: any;
    if (isSubscription) {
      options = {
        ...baseOptions,
        subscription_id: gateway_subscription_id || sub_id,
        recurring: true,
      };
    } else {
      if (!rzp_order?.id) {
        dispatch(
          showMessage({
            type: "error",
            message: "Payment order could not be created. Please try again.",
            displayAs: "snackbar",
          }),
        );
        return;
      }
      options = {
        ...baseOptions,
        order_id: rzp_order.id,
        amount: rzp_order.amount,
        currency: rzp_order.currency,
        recurring: false,
      };
    }

    //@ts-ignore
    const loaded = await loadRazorpay();
    if (!loaded) {
      dispatch(showMessage({ type: "error", message: "Razorpay SDK failed to load", displayAs: "snackbar" }));
      return;
    }
    //@ts-ignore
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", function (failResponse: any) {
      dispatch(
        showMessage({
          type: "error",
          message: failResponse?.error?.description || "Payment failed. Please try again.",
          displayAs: "snackbar",
        }),
      );
    });
    rzp.open();
  };

  const handleConfirm = async (plan: ISubscriptionPlan) => {
    try {
      if (!plan) return;
      setUpserting(true);
      dispatch(saveLoaderProgress());

      const addonsPayload = Object.values(selectedAddons)
        .map(({ addon, addon_count }) => {
          const wanted = "INR";
          const row = addon.price?.find((p) => String(p.currency || "").toUpperCase() === wanted) ?? addon.price?.[0];
          return {
            addon_name: addon.addon_name,
            module_master_uuid: addon.module_master_uuid || "",
            table_name: addon.table_name || "",
            table_identifier_column: addon.table_identifier_column || "",
            extra_count: Number(addon_count) || 0,
            addon_price: Number(row?.amount ?? 0),
            currency: String(row?.currency || "INR").toUpperCase(),
          };
        });

      const response = await upserCompanyOutletSubscriptionPlan({
        company_outlet_uuid: outletUuid,
        req_plan_id: getPlanIdFromPlan(plan.price, "INR") || null,
        customer_requested_status: plan.is_trial ? "TRIAL_RQ" : "SUBSCRIPTION_RQ",
        billing_mode: billingMode,
        addons: Object.values(selectedAddons).map(({ addon }) => addon.addon_master_uuid).filter(Boolean) as any,
      });
      dispatch(saveLoaderCompleted());

      if (isAdminAssign) {
        // ASSIGN mode: direct success
        if (response && response.status === 200) {
          dispatch(showMessage({ type: "success", message: "Plan assigned successfully!", displayAs: "snackbar" }));
          setOpenAddonDialog(null);
          setOpenPreviewDialog(null);
          await fetchPlans();
          await fetchOutletPlan();
          onSuccess?.();
        } else if (response && response.status === 404) {
          dispatch(showMessage({ type: "error", message: response.data.message, displayAs: "snackbar" }));
        }
      } else {
        // Decide whether this request needs a payment step at all.
        //  - Trial plan            → never charge.
        //  - Free/zero plan,no addon→ never charge.
        //  - Free/zero plan + paid addon → charge (addons cost money).
        //  - Paid plan             → charge.
        const isTrial = Boolean(plan.is_trial);
        const needsPayment = !isTrial && (!isPlanFree(plan) || hasPaidAddons());

        if (response && response.data.data && needsPayment) {
          // PAYMENT mode: Razorpay
          await launchRazorpayCheckout(response.data.data, plan);
        } else if (response && response.data.data) {
          // NO-PAYMENT mode: activate directly and show success.
          setOpenSuccess(true);
          await fetchOutletPlan();
          onSuccess?.();
        }

        setOpenAddonDialog(null);
        setOpenPreviewDialog(null);
      }
    } catch (err: any) {
      dispatch(showMessage({ type: "error", message: err.response?.data?.message || "Failed to subscribe to plan", displayAs: "snackbar" }));
      onError?.(err.response?.data?.message || "Failed");
    } finally {
      dispatch(saveLoaderCompleted());
      setUpserting(false);
    }
  };

  const handleShareConfirm = async (plan: ISubscriptionPlan) => {
    const addonsPayload = Object.values(selectedAddons)
      .map(({ addon, addon_count }) => {
        const wanted = "INR";
        const row = addon.price?.find((p) => String(p.currency || "").toUpperCase() === wanted) ?? addon.price?.[0];
        return {
          addon_name: addon.addon_name,
          module_master_uuid: addon.module_master_uuid || "",
          table_name: addon.table_name || "",
          table_identifier_column: addon.table_identifier_column || "",
          extra_count: Number(addon_count) || 0,
          addon_price: Number(row?.amount ?? 0),
          currency: String(row?.currency || "INR").toUpperCase(),
        };
      });

    try {
      const tokenRes = await api.post("/authentication/generate-token", { payload: {}, expiry: "5m" });
      const shareToken = tokenRes.data.data.token;

      const sharePayload = {
        token: shareToken,
        payload: {
          req_plan_id: getPlanIdFromPlan(plan.price, "INR") || null,
          customer_requested_status: plan.is_trial ? "TRIAL_RQ" : "SUBSCRIPTION_RQ",
          addons: Object.values(selectedAddons).map(({ addon }) => addon.addon_master_uuid).filter(Boolean),
          plan_name: plan.plan_name,
          company_outlet_uuid: outletUuid,
        },
      };

      const encoded = btoa(JSON.stringify(sharePayload));
      const shareLink = `${window.location.origin}/public/subscribe?data=${encodeURIComponent(encoded)}`;

      if (navigator.share) {
        navigator.share({ title: `${plan.plan_name} - Subscription Plan`, url: shareLink })
          .catch(() => {
            navigator.clipboard.writeText(shareLink);
            dispatch(showMessage({ type: "success", message: "Share link copied to clipboard!", displayAs: "snackbar" }));
          });
      } else {
        navigator.clipboard.writeText(shareLink).then(() => {
          dispatch(showMessage({ type: "success", message: "Share link copied to clipboard!", displayAs: "snackbar" }));
        }).catch(() => {
          dispatch(showMessage({ type: "error", message: "Failed to copy link", displayAs: "snackbar" }));
        });
      }
    } catch (err: any) {
      dispatch(showMessage({ type: "error", message: err.response?.data?.message || "Failed to generate share link", displayAs: "snackbar" }));
    }
  };

  const getButtonText = (plan: ISubscriptionPlan, isCurrentPlan: boolean, isPaymentPending: boolean) => {
    if (isPaymentPending) return "Payment in Progress";
    if (isCurrentPlan) return "Cancel";
    if (!resolvedSelectedPlanId) return "Add Plan";
    const currentPlan = displayedPlans.find((p) => p.subscription_plan_uuid === resolvedSelectedPlanId);
    const currentPrice = currentPlan && Array.isArray(currentPlan.price) ? Number(currentPlan.price[0]?.base_amount ?? 0) : 0;
    const planPrice = Array.isArray(plan.price) ? Number(plan.price[0]?.base_amount ?? 0) : 0;
    return planPrice < currentPrice ? "Downgrade Plan" : "Upgrade Plan";
  };

  // --- SHARED CONTENT ---
  const planContent = (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        height: "100%",
        overflowX: "hidden",
        pb: 5,
        pt: 3, px: 2,
        ...(location.pathname === "/subscription/pricing" && { marginTop: "60px" }),
      }}
    >

      {!isAdminAssign && urlMessage && !(location.pathname === "/subscription/pricing") && (
        <Alert
          severity="warning"
          icon={<WarningAmber sx={{ fontSize: 28 }} />}
          sx={{ mb: 4, maxWidth: 800, mx: "auto", borderRadius: 3, boxShadow: "0 4px 12px rgba(255, 152, 0, 0.15)", border: "1px solid #ffb74d", bgcolor: "#fff8e1", "& .MuiAlert-icon": { color: "#f57c00", alignItems: "center" }, "& .MuiAlert-message": { width: "100%" } }}
        >
          <Typography variant="body1" sx={{ color: "#5d4037", fontWeight: 500, fontSize: "1.2rem", lineHeight: 1.6 }}>
            {urlMessage}
          </Typography>
        </Alert>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ flexWrap: "wrap" }}
      >
        <Typography variant="h2" sx={{ fontWeight: "bold", textAlign: "left" }}>
          {resolvedSelectedPlanId ? "Subscription Plans" : "Available Subscription Plans"}
        </Typography>
        <Box sx={{ position: "relative", display: "inline-flex", ml: "auto" }}>
          <ToggleButtonGroup
            value={billingCycleFilter}
            exclusive
            size="small"
            onChange={(_e, value) => { if (value) setBillingCycleFilter(value); }}
            sx={{
              bgcolor: PRICING_ACCENT.soft,
              borderRadius: 999,
              p: 0.4,
              gap: 0.4,
              "& .MuiToggleButtonGroup-grouped": {
                border: 0,
                borderRadius: "999px !important",
              },
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.35,
                fontSize: "0.78rem",
                textTransform: "none",
                fontWeight: 700,
                color: PRICING_ACCENT.main,
                "&.Mui-selected": {
                  bgcolor: PRICING_ACCENT.main,
                  color: "#fff",
                  "&:hover": { bgcolor: PRICING_ACCENT.dark },
                },
              },
            }}
          >
            <ToggleButton value="months">Monthly</ToggleButton>
            <ToggleButton value="years">Yearly</ToggleButton>
          </ToggleButtonGroup>

          {/* Discount badge pinned to the top-right of the Yearly button */}
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: -10,
              right: -14,
              px: 0.9,
              py: 0.2,
              borderRadius: 999,
              fontSize: "0.62rem",
              fontWeight: 700,
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              color: "#fff",
              bgcolor: PRICING_ACCENT.main,
              boxShadow: "0 2px 6px rgba(180, 49, 74, 0.4)",
              border: "1.5px solid #fff",
              transform: "rotate(0deg)",
            }}
          >
            Save ~17%
          </Box>
        </Box>
      </Stack>

      {subscriptionLoading ? (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "stretch",
            gap: 2,
          }}
        >
          {Array.from({ length: 3 }).map((_item, index) => (
            <Box key={index} sx={{ width: { xs: "100%", sm: 300 }, maxWidth: 300 }}>
              <PricingPlanCardSkeleton highlighted={index === 1} />
            </Box>
          ))}
        </Box>
      ) : (
        <>
          {resolvedSelectedPlanId && (
            <Typography variant="h4" color="text.secondary" sx={{ mb: 4, textAlign: "left" }}>
              {subscriptionError ? subscriptionError.message : "Your current plan is highlighted below. Explore other options to upgrade or switch."}
            </Typography>
          )}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "stretch",
              gap: 2,
            }}
          >
            {displayedPlans?.map((plan, index) => {
              const isCurrentPlan = Boolean(resolvedSelectedPlanId && plan.subscription_plan_uuid === resolvedSelectedPlanId);
              const isPaymentPending = subscriptions.nextSubscriptionPlan?.subscription_plan_uuid === plan.subscription_plan_uuid && subscriptions.nextSubscriptionPlan?.status === "PENDING";

              // Highlight the middle tier when there is no active plan yet
              // (mirrors the "PROFESSIONAL" emphasis in the mockup).
              const highlighted =
                !resolvedSelectedPlanId &&
                displayedPlans.length >= 3 &&
                index === 1;
              const previousPlanName =
                index > 0 ? displayedPlans[index - 1]?.plan_name : undefined;

              return (
                <Box
                  key={plan.subscription_plan_uuid || index}
                  sx={{ width: { xs: "100%", sm: 300 }, maxWidth: 300, display: "flex" }}
                >
                  <PricingPlanCard
                    plan={plan}
                    index={index}
                    previousPlanName={previousPlanName}
                    highlighted={highlighted}
                    handleChoosePlan={isCurrentPlan ? undefined : handleChoosePlan}
                    onShare={showShareButton ? handleSharePlan : undefined}
                    cardButtonText={getButtonText(plan, isCurrentPlan, isPaymentPending)}
                    isCurrentPlan={isCurrentPlan}
                    isPaymentPending={isPaymentPending}
                    currentBillingMode={
                      isCurrentPlan
                        ? (subscribed_plan?.billing_mode as CurrentBillingMode) || "RECURRING"
                        : undefined
                    }
                    onChangeBillingMode={
                      isCurrentPlan && !isAdminAssign
                        ? (p) => setChangeModePlan(p)
                        : undefined
                    }
                  />
                </Box>
              );
            })}
          </Box>
          {displayedPlans.length === 0 && (
            <Typography variant="h4" color="text.secondary" sx={{ textAlign: "center", py: 8 }}>
              No {billingCycleFilter === "years" ? "yearly" : "monthly"} plans available.
            </Typography>
          )}

          {trialPlan && (
            <Box sx={{ width: "100%", maxWidth: 964, mx: "auto", boxSizing: "border-box" }}>
              <TrialPlanBanner
                plan={trialPlan}
                isTrialActive={Boolean(
                  resolvedSelectedPlanId &&
                  resolvedSelectedPlanId === trialPlan.subscription_plan_uuid,
                )}
                loading={upserting}
                onContinue={handleConfirm}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );

  // --- ADDON DIALOG ---
  const addonDialog = openAddonDialog && (
    <Dialog
      open={true}
      onClose={() => setOpenAddonDialog(null)}
      size="lg"
      title={addonDialogType === "SHARE" ? "Share plan with add-ons" : "Choose add-ons"}
      subtitle={
        addonDialogType === "SHARE"
          ? "Pick add-ons to include in the shared plan link"
          : "Enhance your plan with optional add-ons"
      }
      headerIcon={<LocalOfferOutlinedIcon />}
      titleSx={dialogAccentTitleSx}
      actions={[
        { label: "Cancel", type: "button", variant: "text", disabled: upserting, onClick: () => setOpenAddonDialog(null) },
        {
          label: addonDialogType === "SHARE" ? "Share" : "Continue",
          type: "button",
          variant: "contained",
          disabled: upserting,
          sx: dialogAccentButtonSx,
          onClick: () => {
            if (addonDialogType === "SHARE") {
              handleShareConfirm(openAddonDialog);
              return;
            }
            // Free/zero plan with no paid add-ons needs no order summary or
            // payment step — subscribe directly.
            if (isPlanFree(openAddonDialog) && !hasPaidAddons()) {
              handleConfirm(openAddonDialog);
              return;
            }
            setOpenPreviewDialog(openAddonDialog);
          },
        },
      ]}
    >
      <Stack spacing={2}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Select add-ons for{" "}
          <Typography component="span" variant="h4" color="primary.main">{openAddonDialog.plan_name}</Typography>
        </Typography>

        {addonLoadState === "loading" && (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="h5">Loading add-ons…</Typography>
          </Stack>
        )}

        {addonLoadState !== "loading" && availableAddons.length === 0 && (
          <Typography variant="h5" color="text.secondary">No add-ons available.</Typography>
        )}

        {availableAddons.length > 0 && (
          <Box sx={{ maxHeight: 480, overflow: "auto", pr: 1 }}>
            <Grid container spacing={1.5}>
              {availableAddons
                .filter((addon) => !addon.billing_cycle || !openAddonDialog?.billing_cycle || addon.billing_cycle === openAddonDialog.billing_cycle)
                .map((addon) => {
                  const key = addon.addon_master_uuid || `${addon.addon_name}::${addon.module_endpoint}`;
                  const checked = Boolean(selectedAddons[key]);
                  const price = getAddonPrice(addon, "INR");
                  const { intro, features } = parseAddonDescription(addon.description);

                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <Paper
                        onClick={() => toggleAddon(key, addon)}
                        role="button"
                        tabIndex={0}
                        variant="outlined"
                        sx={{
                          p: 2, height: "100%", borderRadius: 3, cursor: "pointer",
                          borderColor: checked ? "primary.main" : "divider",
                          borderWidth: checked ? 2 : 1,
                          bgcolor: checked ? (t) => `${t.palette.primary.main}12` : "background.paper",
                          transition: "all 140ms ease",
                          "&:hover": { borderColor: checked ? "primary.dark" : "grey.400", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
                        }}
                      >
                        <Stack spacing={1.5} sx={{ height: "100%" }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                              <Avatar sx={{ width: 44, height: 44, bgcolor: checked ? (t) => `${t.palette.primary.main}15` : "grey.100", color: checked ? "primary.main" : "text.secondary", borderRadius: 2 }}>
                                <GroupAddOutlinedIcon />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>{addon.addon_name}</Typography>
                                <Typography variant="h6" color={checked ? "primary.main" : "text.secondary"} sx={{ fontWeight: 700 }}>₹{price}</Typography>
                              </Box>
                            </Stack>
                            <Chip
                              label={checked ? "Selected" : "Select"}
                              color={checked ? "primary" : "default"}
                              variant={checked ? "filled" : "outlined"}
                              sx={{ flexShrink: 0, height: 32, fontSize: "0.85rem", fontWeight: 600, px: 0.5, ...(checked ? {} : { borderColor: "grey.300", color: "text.secondary" }) }}
                            />
                          </Stack>
                          <Box sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                            {intro && <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>{intro}</Typography>}
                            {features.length > 0 && (
                              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1.5, rowGap: 0.5 }}>
                                {features.map((feature, fIdx) => (
                                  <Stack key={fIdx} direction="row" spacing={0.5} alignItems="flex-start">
                                    <CheckCircleIcon sx={{ fontSize: 16, color: checked ? "primary.main" : "success.main", mt: "2px", flexShrink: 0 }} />
                                    <Typography variant="body2">{feature}</Typography>
                                  </Stack>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
            </Grid>
          </Box>
        )}
      </Stack>
    </Dialog>
  );

  // --- BREADCRUMB (admin mode) ---
  const BCrumb: IBreadcrumbProps["items"] = [
    { to: "/dashboard", title: "Dashboard" },
    { to: "/company-outlet", title: "Company Outlet" },
    { title: getStepText },
  ];

  // --- CONDITIONAL WRAPPER RENDER ---
  if (isAdminAssign) {
    return (
      <PageContainer title={getStepText} description="Manage subscription plans and billing">
        <Breadcrumb title="Subscription" items={BCrumb} />
        <Paper
          variant="outlined"
          sx={{ borderColor: "primary.main", pl: "24px", pr: "24px", py: 2, borderRadius: 2, mb: 3, width: "97%", bgcolor: "transparent", mx: 3 }}
        >
          <Typography variant="h6" sx={{ color: "primary.main", textAlign: "left", fontWeight: 400 }}>
            Currently viewing subscription plans for {companyOutletData?.company_outlet_name || "..."}
          </Typography>
        </Paper>
        <Box sx={{ mt: 2 }}>
          {planContent}
        </Box>

        {addonDialog}

        {openPreviewDialog && (
          <OrderPreviewDialog
            open={true}
            plan={openPreviewDialog}
            selectedAddons={selectedAddons}
            currency="INR"
            loading={upserting}
            billingMode={billingMode}
            onBillingModeChange={setBillingMode}
            onClose={() => setOpenPreviewDialog(null)}
            onConfirmAndPay={async () => { await handleConfirm(openPreviewDialog); }}
            accentTitleSx={dialogAccentTitleSx}
            accentButtonSx={dialogAccentButtonSx}
          />
        )}

        {openSuccess && (
          <SuccessDialog
            content="Plan assigned successfully."
            open={openSuccess}
            onClose={() => { setOpenSuccess(false); onSuccess?.(); }}
          />
        )}
      </PageContainer>
    );
  }

  // --- NORMAL USER / SIGNUP WRAPPER ---
  return (
    <>
      {location.pathname === "/subscription/pricing" && (
        <Navbar
          pageType="LOGOUT"
          accentColor={PRICING_ACCENT.main}
          accentHoverColor={PRICING_ACCENT.dark}
        />
      )}

      <PageContainer title={getStepText} description="Manage subscription plans and billing">
        {planContent}

        {addonDialog}

        {openPreviewDialog && (
          <OrderPreviewDialog
            open={true}
            plan={openPreviewDialog}
            selectedAddons={selectedAddons}
            currency="INR"
            loading={upserting}
            billingMode={billingMode}
            onBillingModeChange={setBillingMode}
            onClose={() => setOpenPreviewDialog(null)}
            onConfirmAndPay={async () => { await handleConfirm(openPreviewDialog); }}
            accentTitleSx={dialogAccentTitleSx}
            accentButtonSx={dialogAccentButtonSx}
          />
        )}

        {changeModePlan && (
          <ChangeBillingModeDialog
            open={true}
            onClose={() => setChangeModePlan(null)}
            companyOutletUuid={outletUuid}
            currentBillingMode={(subscribed_plan?.billing_mode as CurrentBillingMode) || "RECURRING"}
            userUuid={user.userUuid}
            plan={changeModePlan}
            onSwitched={() => {
              setChangeModePlan(null);
              fetchPlans();
              checkUserSession();
            }}
          />
        )}

        {openSuccess && (
          <SuccessDialog
            content="You have successfully subscribed to the plan."
            open={openSuccess}
            onClose={() => {
              setOpenSuccess(false);
              navigate("/");
              checkUserSession();
              onSuccess?.();
            }}
          />
        )}
        {paymentSuccessDialog && (
          <SubscriptionpaymentSuccessDialog
            open={paymentSuccessDialog}
            onClose={() => setPaymentSuccessDialog(false)}
          />
        )}
      </PageContainer>
    </>
  );
};
